import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Tabs,
  Tab
} from '@mui/material';
import { Google as GoogleIcon, Person as PersonIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Declare Google API types
declare global {
  interface Window {
    google: any;
  }
}

const GOOGLE_CLIENT_ID = '190617217394-otofkjt9vkpohp9fkke1a999ppfdova3.apps.googleusercontent.com';

const LoginPage = () => {
  const { loginWithGoogle, loginKid, loading, isAuthenticated, parent, currentKid, userType } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [googleReady, setGoogleReady] = useState(false);

  // Kid login form state
  const [kidUsername, setKidUsername] = useState('');
  const [kidPin, setKidPin] = useState('');
  const [kidLoginLoading, setKidLoginLoading] = useState(false);

  // Ref for the Google button container
  const googleButtonRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Redirect if already authenticated
    if (isAuthenticated) {
      if (userType === 'kid' && currentKid) {
        navigate(`/user/${currentKid.id}/targets`);
      } else if (userType === 'parent' && parent) {
        navigate('/select-kid');
      }
    }
  }, [isAuthenticated, parent, currentKid, userType, navigate]);

  const handleGoogleCallback = useCallback(async (response: any) => {
    if (response.credential) {
      try {
        setLoginLoading(true);
        setError(null);
        await loginWithGoogle(response.credential);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Login failed. Please try again.');
      } finally {
        setLoginLoading(false);
      }
    } else {
      // GSI returns no credential for unauthorized origins (and logs only in its own iframe)
      setError(response?.error || 'Google sign-in returned no credential. This domain may not be authorized in the Google Cloud console.');
    }
  }, [loginWithGoogle]);

  useEffect(() => {
    // Load Google Sign-In API
    if (window.google?.accounts?.id) {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCallback,
      });
      setGoogleReady(true);
      return;
    }

    const existingScript = document.getElementById('google-gsi-script');
    if (existingScript) {
      const checkReady = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(checkReady);
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleGoogleCallback,
          });
          setGoogleReady(true);
        }
      }, 100);
      return () => clearInterval(checkReady);
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.id = 'google-gsi-script';
    script.onload = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCallback,
        });
        setGoogleReady(true);
      }
    };
    document.body.appendChild(script);

    return () => {
      // Don't remove script on unmount
    };
  }, [handleGoogleCallback]);

  // Render fallback button into a container (always visible alongside One Tap)
  const renderGoogleButton = useCallback(() => {
    if (!googleButtonRef.current || googleButtonRef.current.hasChildNodes()) return;
    window.google.accounts.id.renderButton(
      googleButtonRef.current,
      {
        theme: 'outline',
        size: 'large',
        width: '100%',
        text: 'signin_with',
        shape: 'rectangular',
      }
    );
  }, []);

  // On parent tab: try One Tap (existing sessions get auto-prompt),
  // and always render the button as fallback for new sessions.
  useEffect(() => {
    if (!googleReady || activeTab !== 0) return;

    window.google.accounts.id.prompt();
    // Always render the button — it covers cases where One Tap
    // isn't shown (new browser, dismissed, or FedCM blocking)
    renderGoogleButton();

    return () => {
      if (googleButtonRef.current) {
        googleButtonRef.current.innerHTML = '';
      }
    };
  }, [googleReady, activeTab, renderGoogleButton]);

  const handleKidLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!kidUsername.trim() || !kidPin.trim()) {
      setError('Please enter both username and PIN');
      return;
    }

    try {
      setKidLoginLoading(true);
      setError(null);
      await loginKid(kidUsername.trim().toLowerCase(), kidPin.trim());
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Login failed. Please try again.';
      setError(errorMessage);
    } finally {
      setKidLoginLoading(false);
    }
  };

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setKidPin(value);
  };

  if (loading || isAuthenticated) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '80vh',
      p: 2
    }}>
      <Card sx={{ maxWidth: 400, width: '100%', borderRadius: 3 }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Box sx={{
            width: 80,
            height: 80,
            bgcolor: 'primary.main',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3
          }}>
            <Typography variant="h3" sx={{ color: 'white' }}>⭐</Typography>
          </Box>

          <Typography variant="h4" gutterBottom fontWeight={700}>
            Points
          </Typography>

          {/* Tabs for Parent/Kid login */}
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => {
              setActiveTab(newValue);
              setError(null);
            }}
            variant="fullWidth"
            sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Parent" />
            <Tab label="Kid" />
          </Tabs>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Parent Login Tab */}
          {activeTab === 0 && (
            <>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Sign in with your Google account to manage your kids' goals and rewards
              </Typography>

              {/* Google Sign-In Button (always visible; One Tap may appear above it) */}
              <Box
                ref={googleButtonRef}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  minHeight: 48,
                  mb: 3,
                  '& > div': { width: '100%' },
                  '& iframe': { borderRadius: 2 },
                }}
              />

              {!googleReady && (
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled
                  startIcon={<CircularProgress size={20} />}
                  sx={{ py: 1.5, bgcolor: '#4285f4' }}
                >
                  Loading Google Sign-In...
                </Button>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Parents can create and manage kid accounts after signing in
              </Typography>
            </>
          )}

          {/* Kid Login Tab */}
          {activeTab === 1 && (
            <>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Enter your name and PIN to see your targets and rewards
              </Typography>

              <Box component="form" onSubmit={handleKidLogin}>
                <TextField
                  fullWidth
                  label="Your Name"
                  value={kidUsername}
                  onChange={(e) => setKidUsername(e.target.value)}
                  margin="normal"
                  required
                  autoFocus
                  autoComplete="username"
                  InputProps={{
                    startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} />
                  }}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="PIN Code"
                  value={kidPin}
                  onChange={handlePinChange}
                  margin="normal"
                  required
                  type="password"
                  inputMode="numeric"
                  autoComplete="current-password"
                  placeholder="6-digit PIN"
                  helperText="Enter your 6-digit PIN"
                />

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={kidLoginLoading || kidPin.length !== 6 || !kidUsername.trim()}
                  sx={{ mt: 3, py: 1.5 }}
                >
                  {kidLoginLoading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
                </Button>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                Ask your parent if you don't have a PIN yet
              </Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
